import type React from "react"
import type { MouseEventHandler } from "react"
import { memo, useCallback } from "react"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"
import { ReactComponent as ArrowLeftIcon } from "../../assets/icons/arrow_left.svg"
import type { ButtonProps } from "../common/types"

type BottomBarProps = {
  layout: "single" | "stacked_back" | "main_back" | "side_by_side" | "message" | "stacked"
  message?: string
  onNext?: (() => void) | string
  onBack?: (() => void) | string
  labelNext?: string
  labelBack?: string
  bordered?: boolean
  onBackParalelEventLink?(): void
  onNextParalelEventLink?(): void
  variantsPrimary?: ButtonProps["variant"]
  variantsSecondary?: ButtonProps["variant"]
  disabledNextBtn?: boolean
  disabledBackBtn?: boolean
  padding?: boolean
  borderSpacing?: boolean
}

const BottomBar: React.FC<BottomBarProps> = ({
  layout,
  onNext,
  onBack,
  message,
  labelBack,
  labelNext = "Next",
  bordered = true,
  onBackParalelEventLink,
  onNextParalelEventLink,
  variantsPrimary = "primary",
  variantsSecondary = "secondary",
  disabledNextBtn = false,
  disabledBackBtn = false,
  padding = true,
  borderSpacing = false,
}) => {
  const layoutVariant = {
    single: "block",
    stacked: "flex flex-col items-center gap-y-4",
    stacked_back: "flex flex-col-reverse items-center gap-y-4",
    main_back: "flex items-stretch gap-x-2",
    side_by_side: "flex items-center gap-x-2",
    message: "block w-full",
  }

  const variantsPrimaryClasses = {
    primary: "btn-primary-lg",
    alert: "btn-alert-lg",
    secondary: "btn-secondary-lg ",
  }
  const variantsSecondaryClasses = {
    primary: "btn-primary-lg",
    alert: "btn-alert-lg",
    secondary: "btn-secondary-lg flex items-center justify-center",
  }

  const isBackBtnVisible = layout === "stacked_back" || layout === "side_by_side" || layout === "stacked"

  const handleBackParalelEvent = useCallback(() => {
    if (onBackParalelEventLink) {
      onBackParalelEventLink()
    }
  }, [onBackParalelEventLink])

  const handleNextParalelEvent = useCallback(() => {
    if (onNextParalelEventLink) {
      onNextParalelEventLink()
    }
  }, [onNextParalelEventLink])

  const isExternalUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://')
  }

  return (
    <div
      className={twMerge(
        "w-full",
        padding ? "p-4" : "p-0",
        bordered && "border-t border-pn_gray-200",
        borderSpacing && "pt-4",
      )}
    >
      <div className={twMerge(layoutVariant[layout as keyof typeof layoutVariant])}>
        {layout === "main_back" &&
          (typeof onBack !== "string" ? (
            <button
              disabled={disabledBackBtn}
              onClick={onBack as MouseEventHandler<HTMLButtonElement>}
              className={twMerge(variantsSecondaryClasses[variantsSecondary])}
            >
              <ArrowLeftIcon />
            </button>
          ) : (
            isExternalUrl(onBack) ? (
              <a
                href={!disabledBackBtn ? (onBack as string) : "#"}
                className={twMerge(
                  variantsSecondaryClasses[variantsSecondary],
                  disabledBackBtn && "pointer-events-none disabled",
                )}
                onClick={!disabledBackBtn ? handleBackParalelEvent : () => { }}
              >
                <ArrowLeftIcon />
              </a>
            ) : (
              <Link
                onClick={!disabledBackBtn ? handleBackParalelEvent : () => { }}
                to={!disabledBackBtn ? (onBack as string) : "#"}
                className={twMerge(
                  variantsSecondaryClasses[variantsSecondary],
                  disabledBackBtn && "pointer-events-none disabled",
                )}
              >
                <ArrowLeftIcon />
              </Link>
            )
          ))}
        {isBackBtnVisible &&
          (typeof onBack !== "string" ? (
            <button
              disabled={disabledBackBtn}
              onClick={onBack as MouseEventHandler<HTMLButtonElement>}
              className={twMerge(variantsSecondaryClasses[variantsSecondary], "w-full")}
            >
              {labelBack}
            </button>
          ) : (
            isExternalUrl(onBack) ? (
              <a
                href={!disabledBackBtn ? (onBack as string) : "#"}
                className={twMerge(
                  variantsSecondaryClasses[variantsSecondary],
                  "w-full text-center",
                  disabledBackBtn && "pointer-events-none disabled",
                )}
                onClick={!disabledBackBtn ? handleBackParalelEvent : () => { }}
              >
                {labelBack}
              </a>
            ) : (
              <Link
                to={!disabledBackBtn ? (onBack as string) : "#"}
                className={twMerge(
                  variantsSecondaryClasses[variantsSecondary],
                  "w-full text-center",
                  disabledBackBtn && "pointer-events-none disabled",
                )}
                onClick={!disabledBackBtn ? handleBackParalelEvent : () => { }}
              >
                {labelBack}
              </Link>
            )
          ))}
        {typeof onNext !== "string" ? (
          <button
            disabled={disabledNextBtn}
            onClick={onNext as MouseEventHandler<HTMLButtonElement>}
            className={twMerge(variantsPrimaryClasses[variantsPrimary], "w-full")}
          >
            {labelNext}
          </button>
        ) : (
          isExternalUrl(onNext) ? (
            <a
              href={!disabledNextBtn ? (onNext as string) : "#"}
              className={twMerge(
                variantsPrimaryClasses[variantsPrimary],
                "w-full text-center block",
                disabledNextBtn && "pointer-events-none disabled",
              )}
              onClick={!disabledNextBtn ? handleNextParalelEvent : () => { }}
            >
              {labelNext}
            </a>
          ) : (
            <Link
              to={!disabledNextBtn ? (onNext as string) : "#"}
              className={twMerge(
                variantsPrimaryClasses[variantsPrimary],
                "w-full text-center block",
                disabledNextBtn && "pointer-events-none disabled",
              )}
              onClick={!disabledNextBtn ? handleNextParalelEvent : () => { }}
            >
              {labelNext}
            </Link>
          )
        )}
      </div>
      {layout === "message" && !!message && (
        <div className="w-full text-center mt-4">
          <span className="text-pn-callout-regular">{message}</span>
        </div>
      )}
    </div>
  )
}
export default memo(BottomBar)
